import React from 'react';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import { fetchOrgDocumentsV2 } from 'api/organizations';
import {
  EventStandardContainer,
  EventStandardContainerData,
} from 'components/EventStandardContainer';
import { IError } from 'utils/handleAxiosError';
import { fetchCommonEventData } from 'api/events';
import { SubmissionsListContainer } from 'containers/SubmissionsList';
import { parseQuery } from 'api/utils';

export type SubmissionsPageProps = InferGetStaticPropsType<typeof getStaticProps>;

const dummyfySubmissionsList = (submissonsListData: OrganizationDocumentsResponse) => {
  // minimum data required for submissions poster grid
  const items = submissonsListData.items.map(item =>
    parseQuery({
      embargoed: item.embargoed,
      archive: item.archive,
      _co_authors: [],
      additional_file_urls: [],
      authors: [],
      bookmarks: [],
      event: 'eventId',
      fileid: 'fileId',
      id: item.id,
      poster_id: 'posterId',
      keywords: [],
      public_access_enabled: false,
      submission_completed: true,
      title: item.title,
      thumb_url: item.thumb_url,
      thumb_url_medium: item.thumb_url_medium,
      thumb_url_large: item.thumb_url_large,
      public_stats: item.public_stats,
      orcid_sync_requested: false,
      orcid_id: 'orcidId',
    }),
  ) as unknown as MorressierPoster[];

  return { ...submissonsListData, items };
};

const fetchSubmissionsListPageData = async (eventId: string) =>
  fetchOrgDocumentsV2({ eventId, query: {} });

const fetchSubmissionsListPageFakeData = async (eventId: string) =>
  dummyfySubmissionsList(await fetchOrgDocumentsV2({ eventId, query: {} }));

const SubmissionsPage: NextPage<SubmissionsPageProps> = ({ data, error }) => (
  <EventStandardContainer
    data={data}
    error={error}
    Component={SubmissionsListContainer}
    fetchEmbargoedPageData={
      data?.event.embargoed ? () => fetchSubmissionsListPageData(data!.event._id) : undefined
    }
  />
);

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' });

export const getStaticProps: GetStaticProps<{
  data?: EventStandardContainerData & {
    data: {
      collection: {
        limit: number;
        offset: number;
        total: number;
      };
      items: MorressierPoster[];
    };
  };
  error?: IError;
}> = async ctx => {
  const eventId = ctx.params?.eventId as string;

  const { eventInfo, tabsConfig } = await fetchCommonEventData(eventId);

  const data = eventInfo.event.embargoed
    ? await fetchSubmissionsListPageFakeData(eventId)
    : await fetchSubmissionsListPageData(eventId);

  return {
    props: {
      data: { ...eventInfo, tabsConfig, data },
    },
    // https://nextjs.org/docs/basic-features/data-fetching#incremental-static-regeneration
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most every minute
    revalidate: 60, // In seconds
  };
};

export default SubmissionsPage;
